import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button, Dropdown, Form, Header, Label, Message, Segment, Table,
} from "semantic-ui-react";
import apis from "../../../apis";
import { formatDate } from "../formatters";

const TYPE_OPTIONS = [
  { key: "applicant", value: "applicant", text: "Applicant ID" },
  { key: "order", value: "order", text: "Order ID" },
  { key: "email", value: "email", text: "Email" },
];

function timelineRows(result) {
  return [
    ...(result?.runs || []).map((run) => ({
      key: `run-${run.automationId}-${run.runId}`,
      at: run.endedAt || run.startedAt,
      automationId: run.automationId,
      runId: run.runId,
      kind: "Run",
      detail: run.state,
    })),
    ...(result?.activities || []).map((activity, index) => ({
      key: `activity-${activity.automationId}-${activity.runId}-${activity.stageCode}-${activity.attempt}-${index}`,
      at: activity.endedAt || activity.startedAt,
      automationId: activity.automationId,
      runId: activity.runId,
      kind: activity.scope === "step" ? "Step" : "Stage",
      detail: `${activity.stageCode}${activity.stepCode ? ` / ${activity.stepCode}` : ""}: ${activity.state}`,
    })),
    ...(result?.activeIncidents || []).map((incident, index) => ({
      key: `incident-${incident.automationId}-${incident.runId || "automation"}-${incident.type}-${index}`,
      at: incident.lastSeenAt,
      automationId: incident.automationId,
      runId: incident.runId,
      kind: "Incident",
      detail: incident.type.replace(/_/g, " "),
    })),
  ].filter((row) => row.at).sort((left, right) => new Date(right.at) - new Date(left.at));
}

const SubjectHistory = ({ defaultEnvironment, onOpenRun }) => {
  const [type, setType] = useState("applicant");
  const [environment, setEnvironment] = useState(defaultEnvironment);
  const [value, setValue] = useState("");
  const [automationFilter, setAutomationFilter] = useState("");
  const [result, setResult] = useState(null);
  const [submittedGeneration, setSubmittedGeneration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestGeneration = useRef(0);

  useEffect(() => () => {
    requestGeneration.current += 1;
  }, []);

  const search = async () => {
    const generation = ++requestGeneration.current;
    setSubmittedGeneration(generation);
    setResult(null);
    setAutomationFilter("");
    setLoading(false);
    const normalizedValue = value.trim();
    if (!environment.trim() || !normalizedValue || (type === "email" && !/^\S+@\S+\.\S+$/.test(normalizedValue))) {
      setError("Enter a valid environment and search value.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await apis.automationMonitoring.searchSubject({
        environment: environment.trim(),
        type,
        value: normalizedValue,
      });
      if (generation === requestGeneration.current) {
        setResult({ data, generation });
        setError("");
      }
    } catch (requestError) {
      if (generation === requestGeneration.current) {
        setError(requestError.response?.data?.message || "Could not find subject history.");
      }
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  };

  const visibleResult = result?.generation === submittedGeneration ? result.data : null;
  const allRows = useMemo(() => timelineRows(visibleResult), [visibleResult]);
  const automationOptions = useMemo(() => Array.from(new Set(allRows.map((row) => row.automationId)))
    .sort()
    .map((id) => ({ key: id, value: id, text: id })), [allRows]);
  const rows = automationFilter ? allRows.filter((row) => row.automationId === automationFilter) : allRows;
  const repeatedFailures = useMemo(() => {
    const counts = {};
    (visibleResult?.activities || []).filter((activity) => activity.state === "failed").forEach((activity) => {
      const key = `${activity.automationId}|${activity.stageCode}|${activity.stepCode || ""}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).filter(([, count]) => count > 1);
  }, [visibleResult]);
  const hasResult = Boolean(visibleResult);
  const isEmpty = hasResult && (visibleResult.subjects || []).length === 0;

  return (
    <Segment className="automation-monitoring__analysis-section">
      <Header as="h2">Subject history</Header>
      <Form className="automation-monitoring__subject-form" onSubmit={search}>
        <Form.Field>
          <label htmlFor="subject-search-type">Search type</label>
          <Dropdown id="subject-search-type" selection options={TYPE_OPTIONS} value={type} onChange={(event, data) => setType(data.value)} />
        </Form.Field>
        <Form.Input label="Environment" value={environment} onChange={(event) => setEnvironment(event.target.value)} />
        <Form.Input label={TYPE_OPTIONS.find((option) => option.value === type)?.text} type={type === "email" ? "email" : "text"} value={value} onChange={(event) => setValue(event.target.value)} />
        <Button type="submit" color="teal" loading={loading}>Search</Button>
      </Form>
      <p className="automation-monitoring__secondary">Only masked email aliases appear here.</p>
      {error ? <Message negative content={error} /> : null}
      {loading && !result ? <Message content="Loading subject history..." /> : null}
      {isEmpty ? <Message content="No subject history was found." /> : null}

      {hasResult && !isEmpty ? (
        <>
          <div className="automation-monitoring__subject-summary">
            {(visibleResult.subjects || []).map((subject) => (
              <div key={subject.id}>
                <strong>{subject.type}: {subject.canonicalId}</strong>
                <span>{(subject.aliases || []).map((alias) => alias.maskedValue).filter(Boolean).join(", ") || "No masked aliases"}</span>
              </div>
            ))}
          </div>
          <div className="automation-monitoring__subject-highlights">
            <div><strong>Current stuck work</strong><span>{(visibleResult.activeIncidents || []).filter((incident) => incident.type === "hard_timeout").length}</span></div>
            <div><strong>Latest completed stages</strong><span>{(visibleResult.latestCompletedStages || []).map((activity) => activity.stageCode).join(", ") || "-"}</span></div>
            <div><strong>Repeated failures</strong><span>{repeatedFailures.length ? repeatedFailures.map(([key, count]) => `${key.split("|").slice(1).filter(Boolean).join(" / ")} (${count})`).join(", ") : "-"}</span></div>
          </div>
          <Dropdown clearable selection placeholder="All automations" options={automationOptions} value={automationFilter} onChange={(event, data) => setAutomationFilter(data.value || "")} />
          {rows.length === 0 ? <Message content="No timeline entries match this automation." /> : (
            <div className="automation-monitoring__table-scroll">
              <Table celled compact>
                <Table.Header><Table.Row><Table.HeaderCell>Time</Table.HeaderCell><Table.HeaderCell>Automation</Table.HeaderCell><Table.HeaderCell>Type</Table.HeaderCell><Table.HeaderCell>Detail</Table.HeaderCell><Table.HeaderCell /></Table.Row></Table.Header>
                <Table.Body>{rows.map((row) => (
                  <Table.Row key={row.key}><Table.Cell>{formatDate(row.at)}</Table.Cell><Table.Cell>{row.automationId}</Table.Cell><Table.Cell><Label basic>{row.kind}</Label></Table.Cell><Table.Cell>{row.detail}</Table.Cell><Table.Cell>{row.runId ? <Button size="small" onClick={() => onOpenRun(row.automationId, row.runId)}>Open run</Button> : "-"}</Table.Cell></Table.Row>
                ))}</Table.Body>
              </Table>
            </div>
          )}
        </>
      ) : null}
    </Segment>
  );
};

SubjectHistory.propTypes = {
  defaultEnvironment: PropTypes.string,
  onOpenRun: PropTypes.func.isRequired,
};

SubjectHistory.defaultProps = {
  defaultEnvironment: "prod",
};

export { timelineRows };
export default SubjectHistory;
